/**
 * ObjectElementField React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import connect from "react-redux/es/connect/connect";

// Material UI
import {withStyles} from "@material-ui/core";

// Material UI components
import InputLabel from '@material-ui/core/InputLabel';

// application components
import ObjectTable from "../listing/ObjectTable";
import ObjectTypeMenu from "../base/ObjectTypeMenu";

// application
import {pushAndEditElement, addElement} from "../../operations/editing";
import {removeFieldElementAction, moveFieldElementAction} from "../../redux/editing/actions";
import {searchTag} from "../../operations/listing";
import {openMediaOverlayForObject} from "../../operations/view";
import {selectDerivedTypes} from "../../redux/types/selectors";


/**
 * ObjectElementField is an editing component for (sub) element fields of objects
 *
 * @author Jens Gruschel
 */
export class ObjectElementField extends React.Component {

    objectTypeMenu = undefined;

    handleAddElement = (id, objectTypes) => event => {
        if (objectTypes.length === 1) {
            const scope = this.props.scope || [];
            this.props.addElement([...scope, id], objectTypes[0]);
        }
        else if (objectTypes.length > 1 && this.objectTypeMenu) {
            this.objectTypeMenu.open(event.currentTarget);
        }
    }

    render() {

        const { classes, fieldDef, refTypes } = this.props;

        const scope = this.props.scope || [];
        const limit = fieldDef.quantity === 'single' ? 1 : (Number.isInteger(fieldDef.quantity) && fieldDef.quantity);
        const fullyOccupied  = limit && this.props.fieldItems.length >= limit;

        const standardHandlers = {
            onSelect: (index, object) => this.props.pushAndEditElement([...scope, fieldDef.id, index], object, this.props.readOnly),
            onEdit: (index, object) => this.props.pushAndEditElement([...scope, fieldDef.id, index], object, this.props.readOnly),
            onRemove: (index, object) => this.props.removeFieldElementAction(this.props.objectId, [...scope, fieldDef.id], index)
        };
        const addHandlers = refTypes.length > 0 && !fullyOccupied ? {
            onAdd: this.handleAddElement(this.props.fieldDef.id, refTypes),
        } : {};
        const listHandlers = limit !== 1 ? {
            onDuplicate: (index, item) => this.props.addElement([...scope, fieldDef.id], item.type, item.fields),
            onMove: (fromIndex, toIndex) => this.props.moveFieldElementAction(this.props.objectId, [...scope, fieldDef.id], fromIndex, toIndex)
        } : {};
        const handlers = {
            ...standardHandlers,
            ...addHandlers,
            ...listHandlers
        };

        return <div className={classes.reference}>
            <InputLabel className={classes.referenceLabel}>{fieldDef.name}</InputLabel>
            <ObjectTable
                items={this.props.fieldItems}
                readOnly={this.props.readOnly}
                id={fieldDef.id}
                label={fieldDef.name}
                {...handlers}
                asField={true}
                pagination={false}
            />
            <ObjectTypeMenu
                ref={component => {this.objectTypeMenu = component}}
                types={refTypes}
                onSelect={typeId => this.props.addElement([...scope, fieldDef.id], typeId)}
            />
        </div>;
    }
}

ObjectElementField.propTypes = {
    onObjectValueChange: PropTypes.func.isRequired,
    fieldDef: PropTypes.object.isRequired,
    objectId: PropTypes.string,
    readOnly: PropTypes.bool,
    value: PropTypes.any
};

const styles = {
    reference: {
        marginTop: 16,
        marginBottom: 8,
    },
    referenceLabel: {
        marginBottom: 8,
        display: "inline-block",
        transform: "translate(8px, 17.5px) scale(0.75)",
        transformOrigin: "top left",
        padding: "0 6px",
        backgroundColor: "white"
    }
};

const mapStateToProps = (state, props) => {
    return {
        refTypes: selectDerivedTypes(state.types, props.fieldDef.reftypes || []),
        fieldItems: props.value || [],
    };
};

export default connect(mapStateToProps, {
    pushAndEditElement, addElement, removeFieldElementAction, moveFieldElementAction,
    searchTag, openMediaOverlayForObject
})(withStyles(styles)(ObjectElementField));